<template>
  <transition name="fade">
    <div v-if="visible" class="fixed" @click="closePopup">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <div class="flex items-center gap-sm">
            <span class="badge badge-neutral">Código Reserva</span>
            <code class="code-badge">{{ props.purchaseData.purchase.codigo }}</code>
          </div>
          <button type="button" class="close-btn" @click="closePopup">&times;</button>
        </div>

        <div class="user-card" style="margin-bottom: 1.25rem;">
          <div class="user-info">
            <img :src="purchaseData.user.foto" alt="Imagen del usuario" class="circular-image" />
            <div class="user-text">
              <h3>{{ purchaseData.user.nombre }}</h3>
              <p>{{ purchaseData.user.email }}</p>
            </div>
          </div>
        </div>

        <div class="questions-box" v-if="containsQuestions">
          <h4 style="font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-color-light); margin-bottom: 0.5rem;">
            Información solicitada
          </h4>
          <div v-for="item in purchaseData.purchase.campos" :key="item.orden" style="font-size: 13.5px; margin-bottom: 0.35rem;">
            <strong style="color: var(--text-color-dark);">{{ item.pregunta }}:</strong> {{ item.respuesta }}
          </div>
        </div>

        <div class="products-section">
          <h4 style="font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-color-light); margin-bottom: 0.75rem;">
            Productos comprados
          </h4>
          <PurchasedProductDetails 
            v-for="product in purchaseData.purchase.productos"
            :key="product.id_usuario_producto"
            :status="purchaseData.purchase.estado"
            :product="product"
            :fecha_validez="purchaseData.purchase.fecha_validez"
            @update:quantity="quantityUpdated(product.id_usuario_producto, $event)"
            ref="productDetailsReferences"
          />
        </div>

        <div class="flex flex-col gap-sm" style="margin-top: 1.5rem;">
          <button @click="selectAll" class="secondary_button w-full" :class="selectAllClass" style="padding: 10px;">
            Seleccionar todo
          </button>
          <button @click="redeemProducts" class="main_button-green w-full" :class="redeemButtonClass" style="padding: 12px;">
            Canjear Selección
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>
  
  <script setup>
  import { ref, watch, defineEmits, defineProps, computed } from 'vue';
  import PurchasedProductDetails from "@/components/common/PurchasedProductDetails.vue";
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter } from 'vue-router';

  const props = defineProps({
    purchaseData: Object,
  });
  
  const visible = ref(false);
  const containsQuestions = ref(false);
  const products = ref(null);
  const productDetailsReferences = ref([]);
  const loadingStore = useLoadingStore();
  const emit = defineEmits(['errorRedeeming', 'successRedeeming']);
  const router = useRouter();

  watch(() => props.purchaseData, (newVal) => {
    visible.value = !!newVal;
    containsQuestions.value = newVal?.purchase?.campos.length > 0;
    products.value = newVal?.purchase?.productos;
  });
  
  const selectAllClass = computed(() => {
    var isEnabled = false;
    products.value.forEach(p => {
      if (p.cantidad > (p.cantidad_canjeada + (p.cantidad_para_canjear ?? 0))) {
        isEnabled = true;
      }
    });
    if (props.purchaseData.purchase.estado === 'Caducado') {
      isEnabled = false;
    }
    return isEnabled ? "button-enabled": "button-disabled";
  });

  const redeemButtonClass = computed(() => {
    var isEnabled = false;
    products.value.forEach(p => {
      if (p.cantidad_para_canjear > 0)
        isEnabled = true;
      }
    );
    return isEnabled ? "button-enabled": "button-disabled";
  });

  const quantityUpdated = (productId, quantity) => {
    products.value.find(p => p.id_usuario_producto === productId).cantidad_para_canjear = quantity;
  };

  const closePopup = () => {
    visible.value = false;
    history.back();
  };

  const selectAll = () => {
    if (props.purchaseData.purchase.estado !== 'Caducado') {
      productDetailsReferences.value.forEach(p => p.selectAllExecuted());
    }
  }

const redeemProducts = async () => {
  var products = [];
  props.purchaseData.purchase.productos.forEach(p => {
    if (p.cantidad_para_canjear ?? 0 > 0) {
      products.push({
        "id_usuario_producto": p.id_usuario_producto,
        "cantidad": p.cantidad_para_canjear ?? 0
      });
    }
  });
  if (products.length === 0) {
    return;
  }
  loadingStore.setLoading(true);
  try {
    let response = await fetchRedeemProducts(props.purchaseData.purchase.codigo, products);

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchRedeemProducts(props.purchaseData.purchase.codigo, products);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      emit('errorRedeeming', "Se ha producido un error canjeando la reserva.");
    }

    const data = await response.json();

    if (data.errors) {
      loadingStore.setLoading(false);
      emit('errorRedeeming', data.message);
    } else {
      loadingStore.setLoading(false);
      emit('successRedeeming');
    }
  } catch (exception) {
    loadingStore.setLoading(false);
    emit('errorRedeeming', exception.message);
  } finally {
    visible.value = false;
    history.back();
  }
};

async function fetchRedeemProducts(codigo, products) {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/redeems/redeem_purchase_code", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "purchase_code": codigo,
        "products": products
      }),
    });
}

  </script>
  
  <style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.modal-card {
  background-color: var(--white);
  padding: 1.75rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-xl);
  width: 90%;
  max-width: 600px;
  max-height: 88vh;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from { transform: translateY(12px) scale(0.98); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid var(--border-color-subtle);
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--text-color-light);
  cursor: pointer;
  padding: 0 4px;
  transition: color var(--transition-fast);
}

.close-btn:hover {
  color: var(--text-color-dark);
}

.code-badge {
  font-weight: 700;
  font-size: 30px;
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  color: var(--text-color-dark);
}

.user-card {
  background: var(--very-clear-gray);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1rem 1.25rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.circular-image {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--white);
  box-shadow: var(--shadow-xs);
}

.user-text h3 {
  font-size: 1.05rem;
  margin-bottom: 2px;
  color: var(--text-color-dark);
}

.user-text p {
  font-size: 13px;
  margin-bottom: 0;
  color: var(--text-color-light);
}

.questions-box {
  background: #FFFFFF;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 1.25rem;
}

.products-section {
  margin-top: 1rem;
}

.button-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-enabled {
  opacity: 1;
}
</style>
  